/**
 * LoginPage — sign in, sign up, verify email, set first password and reset password.
 *
 * Layout follows the enforza-neon console login: a split pane with the brand,
 * headline and tagline on the left and the auth card on the right, over a
 * full-width footer band. It stacks to one column on narrow screens.
 * All colours come from src/brand.ts via CSS custom properties (see login.css).
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowRight, Info, Lock, Mail } from "lucide-react";
import { brand } from "../brand";
import { passwordAuthConfigured } from "../config";
import { useAuth } from "../auth/AuthContext";
import * as cognito from "../auth/cognito";
import { beginSsoSignIn, enabledSsoProviders, type SsoProvider } from "../auth/sso";
import { BrandMark, PoweredBy } from "../components/BrandMark";
import { GitHubGlyph, GoogleGlyph, MicrosoftGlyph } from "../components/SsoGlyphs";
import "../login.css";
import { track } from "../track";

type Flow = "login" | "signup" | "confirm" | "new-password" | "forgot" | "reset";

// Card heading per flow: short and plain, no marketing tone
const TITLES: Record<Flow, { title: string; subtitle: string }> = {
  login: { title: "Sign in", subtitle: "Welcome back. Sign in to manage your website components and quotes." },
  signup: { title: "Create account", subtitle: "Start turning visitors into leads." },
  confirm: { title: "Verify email", subtitle: "We sent a 6-digit code to your inbox." },
  "new-password": { title: "Set password", subtitle: "Replace the temporary password from your invite." },
  forgot: { title: "Reset password", subtitle: "We'll email you a code to reset it." },
  reset: { title: "Choose a new password", subtitle: "Enter the code we emailed and your new password." },
};

// SSO providers to show. Until Google/Microsoft are configured in Cognito the
// buttons are shown greyed out with a "Soon" chip so the layout is final.

export function LoginPage() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [flow, setFlow] = useState<Flow>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [challengeSession, setChallengeSession] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(() => searchParams.get("sso_error"));
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Where to go after signing in (set by RequireAuth when it redirected here)
  const next = searchParams.get("next") ? decodeURIComponent(searchParams.get("next")!) : "/";

  /** Switch flow, clearing messages and anything secret from the previous step. */
  function go(to: Flow, message: string | null = null) {
    setFlow(to);
    setError(null);
    setSuccess(message);
    setPassword("");
    setConfirmPassword("");
    setCode("");
    setAcceptedTerms(false);
  }

  /** Run a form action with shared loading + error handling. */
  async function run(e: FormEvent, action: () => Promise<void>) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Passwords must match wherever the user types a new one
  function requireMatch() {
    if (password !== confirmPassword) throw new Error("Passwords do not match.");
  }

  const handleLogin = (e: FormEvent) =>
    run(e, async () => {
      const result = await cognito.signIn(email, password);
      if ("challenge" in result) {
        // Invited user signing in with a temporary password
        setChallengeSession(result.session);
        go("new-password");
        return;
      }
      setSession(result);
      track("login", { method: "email" });
      navigate(next, { replace: true });
    });

  const handleSignup = (e: FormEvent) =>
    run(e, async () => {
      requireMatch();
      if (!acceptedTerms) throw new Error("Please accept the Terms and Privacy Policy to continue.");
      await cognito.signUp(email, password);
      go("confirm");
    });

  const handleConfirm = (e: FormEvent) =>
    run(e, async () => {
      await cognito.confirmSignUp(email, code);
      track("sign_up", { method: "email" });
      go("login", "Email verified. You can now sign in.");
    });

  const handleNewPassword = (e: FormEvent) =>
    run(e, async () => {
      requireMatch();
      if (!challengeSession) {
        go("login");
        throw new Error("Your sign-in session expired. Please sign in again.");
      }
      setSession(await cognito.completeNewPassword(email, challengeSession, password));
      navigate(next, { replace: true });
    });

  const handleForgot = (e: FormEvent) =>
    run(e, async () => {
      await cognito.forgotPassword(email);
      go("reset");
    });

  const handleReset = (e: FormEvent) =>
    run(e, async () => {
      requireMatch();
      await cognito.confirmForgotPassword(email, code, password);
      go("login", "Password updated. Sign in with your new password.");
    });

  async function handleSso(provider: SsoProvider) {
    setError(null);
    try {
      await beginSsoSignIn(provider, next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Single sign-on isn't available.");
    }
  }

  return (
    <div className="login-page">
      {/* ── Split pane: brand story left, auth card right ─────────────────── */}
      <div className="login-grid">
        <div className="login-rail">
          <div className="login-rail-brand">
            <BrandMark size={56} subtitle={brand.productSubtitle} />
          </div>
          <div className="login-hero">
            <h2>
              {brand.login.headline.split("\n").map((line, i) => (
                <span key={i}>
                  {line}
                  <br />
                </span>
              ))}
            </h2>
            {/* Lime accent bar anchors the hero copy */}
            <span className="login-accent-bar" aria-hidden="true" />
            {/* Each line of the tagline is its own paragraph (the last line is the strapline) */}
            {brand.login.tagline.split("\n").map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
          <div className="login-rail-spacer" />
          <div className="login-decor">
            <RailDecor />
          </div>
        </div>

        <div className="login-card-wrap">
          <div className="login-card">
            <div className="login-card-head">
              <h1>{TITLES[flow].title}</h1>
              <p>{TITLES[flow].subtitle}</p>
            </div>
            <div className="login-hairline" />

            {!passwordAuthConfigured && (
              <Flash tone="info">Sign-in isn't connected in this environment yet. The page is shown for layout only.</Flash>
            )}
            {success && <Flash tone="success">{success}</Flash>}
            {error && <Flash tone="danger">{error}</Flash>}

            {flow === "login" && (
              <form onSubmit={handleLogin} noValidate>
                <Fields>
                  <Field label="Email" htmlFor="email">
                    <IconInput Icon={Mail} id="email" type="email" autoComplete="email" value={email} onChange={setEmail} placeholder="you@company.co.uk" disabled={loading} />
                  </Field>
                  <Field
                    label="Password"
                    htmlFor="password"
                    trailing={
                      <button type="button" className="link-button small" onClick={() => go("forgot")}>
                        Forgot password?
                      </button>
                    }
                  >
                    <IconInput Icon={Lock} id="password" type="password" autoComplete="current-password" value={password} onChange={setPassword} placeholder="••••••••" disabled={loading} />
                  </Field>
                </Fields>
                <Submit disabled={loading || !email || !password} loading={loading} idle="Sign in" busy="Signing in…" />
                <OrDivider />
                <SsoButtons onClick={handleSso} disabled={loading} />
              </form>
            )}

            {flow === "signup" && (
              <form onSubmit={handleSignup} noValidate>
                <Fields>
                  <Field label="Work email" htmlFor="signup-email">
                    <IconInput Icon={Mail} id="signup-email" type="email" autoComplete="email" value={email} onChange={setEmail} placeholder="you@company.co.uk" disabled={loading} />
                  </Field>
                  <Field label="Password" htmlFor="signup-password" hint="At least 8 characters, with upper and lower case letters and a number.">
                    <IconInput Icon={Lock} id="signup-password" type="password" autoComplete="new-password" value={password} onChange={setPassword} placeholder="••••••••" disabled={loading} />
                  </Field>
                  <Field label="Confirm password" htmlFor="signup-confirm">
                    <IconInput Icon={Lock} id="signup-confirm" type="password" autoComplete="new-password" value={confirmPassword} onChange={setConfirmPassword} placeholder="••••••••" disabled={loading} />
                  </Field>
                </Fields>
                <label className="terms">
                  <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} disabled={loading} />
                  <span>
                    I accept the{" "}
                    <a href={brand.login.termsUrl} target="_blank" rel="noreferrer">
                      Terms
                    </a>{" "}
                    and{" "}
                    <a href={brand.login.privacyUrl} target="_blank" rel="noreferrer">
                      Privacy Policy
                    </a>
                    .
                  </span>
                </label>
                <Submit disabled={loading || !email || !password || !confirmPassword || !acceptedTerms} loading={loading} idle="Create account" busy="Creating account…" />
                <OrDivider />
                <SsoButtons onClick={handleSso} disabled={loading} />
              </form>
            )}

            {flow === "confirm" && (
              <form onSubmit={handleConfirm} noValidate>
                <p className="login-note">
                  Code sent to <strong>{email}</strong>.
                </p>
                <Fields>
                  <Field label="Verification code" htmlFor="otp">
                    <input className="input code-input" id="otp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" disabled={loading} />
                  </Field>
                </Fields>
                <Submit disabled={loading || code.length < 6} loading={loading} idle="Verify account" busy="Verifying…" />
              </form>
            )}

            {flow === "new-password" && (
              <form onSubmit={handleNewPassword} noValidate>
                <p className="login-note">
                  Welcome. Choose a password for <strong>{email}</strong>.
                </p>
                <Fields>
                  <Field label="New password" htmlFor="new-password">
                    <IconInput Icon={Lock} id="new-password" type="password" autoComplete="new-password" value={password} onChange={setPassword} placeholder="••••••••" disabled={loading} autoFocus />
                  </Field>
                  <Field label="Confirm new password" htmlFor="new-password-confirm">
                    <IconInput Icon={Lock} id="new-password-confirm" type="password" autoComplete="new-password" value={confirmPassword} onChange={setConfirmPassword} placeholder="••••••••" disabled={loading} />
                  </Field>
                </Fields>
                <Submit disabled={loading || !password || !confirmPassword} loading={loading} idle="Set password and sign in" busy="Setting password…" />
              </form>
            )}

            {flow === "forgot" && (
              <form onSubmit={handleForgot} noValidate>
                <Fields>
                  <Field label="Email" htmlFor="forgot-email">
                    <IconInput Icon={Mail} id="forgot-email" type="email" autoComplete="email" value={email} onChange={setEmail} placeholder="you@company.co.uk" disabled={loading} autoFocus />
                  </Field>
                </Fields>
                <Submit disabled={loading || !email} loading={loading} idle="Email me a code" busy="Sending…" />
              </form>
            )}

            {flow === "reset" && (
              <form onSubmit={handleReset} noValidate>
                <p className="login-note">
                  Code sent to <strong>{email}</strong>.
                </p>
                <Fields>
                  <Field label="Reset code" htmlFor="reset-code">
                    <input className="input code-input" id="reset-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" disabled={loading} />
                  </Field>
                  <Field label="New password" htmlFor="reset-password">
                    <IconInput Icon={Lock} id="reset-password" type="password" autoComplete="new-password" value={password} onChange={setPassword} placeholder="••••••••" disabled={loading} />
                  </Field>
                  <Field label="Confirm new password" htmlFor="reset-confirm">
                    <IconInput Icon={Lock} id="reset-confirm" type="password" autoComplete="new-password" value={confirmPassword} onChange={setConfirmPassword} placeholder="••••••••" disabled={loading} />
                  </Field>
                </Fields>
                <Submit disabled={loading || code.length < 6 || !password || !confirmPassword} loading={loading} idle="Update password" busy="Updating…" />
              </form>
            )}

            {/* Switch between flows */}
            <div className="login-switch">
              {flow === "login" && (
                <>
                  No account yet?{" "}
                  <button type="button" className="link-button" onClick={() => go("signup")}>
                    Create one
                  </button>
                </>
              )}
              {flow === "signup" && (
                <>
                  Already have an account?{" "}
                  <button type="button" className="link-button" onClick={() => go("login")}>
                    Sign in
                  </button>
                </>
              )}
              {flow !== "login" && flow !== "signup" && (
                <button type="button" className="link-button" onClick={() => go("login")}>
                  ← Back to sign in
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer band ───────────────────────────────────────────────────── */}
      <footer className="login-footer">
        <PoweredBy />
        <div className="login-footer-links">
          <span>
            © {new Date().getFullYear()} {brand.legalName}
          </span>
          <a href={brand.login.termsUrl} target="_blank" rel="noreferrer">
            Terms
          </a>
          <a href={brand.login.privacyUrl} target="_blank" rel="noreferrer">
            Privacy
          </a>
        </div>
      </footer>
    </div>
  );
}

// ─── Building blocks ──────────────────────────────────────────────────────────

function Fields({ children }: { children: ReactNode }) {
  return <div className="fields">{children}</div>;
}

function Field({ label, htmlFor, trailing, hint, children }: { label: string; htmlFor: string; trailing?: ReactNode; hint?: string; children: ReactNode }) {
  return (
    <div>
      <div className="field-head">
        <label htmlFor={htmlFor}>{label}</label>
        {trailing}
      </div>
      {children}
      {hint && <p className="field-hint">{hint}</p>}
    </div>
  );
}

// Text input drawn as an underline (the login card style); Icon is kept for the call sites
function IconInput({
  Icon,
  onChange,
  ...rest
}: { Icon: typeof Mail; onChange: (v: string) => void } & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  return (
    <div className="line-input">
      <input {...rest} className="line-input-field" onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function Submit({ disabled, loading, idle, busy }: { disabled: boolean; loading: boolean; idle: string; busy: string }) {
  return (
    <button type="submit" className="btn btn-primary btn-block" disabled={disabled}>
      {loading ? (
        <>
          <span className="spinner" aria-hidden="true" />
          {busy}
        </>
      ) : (
        <>
          {idle}
          <ArrowRight size={16} aria-hidden="true" />
        </>
      )}
    </button>
  );
}

function OrDivider() {
  return (
    <div className="or-divider" aria-hidden="true">
      <span />
      or
      <span />
    </div>
  );
}

// SSO: one icon button per provider in a row (Google, Microsoft, GitHub). Only
// providers set up on our Cognito pool are active; the rest are greyed out
// with a "coming soon" tooltip.
const SSO_ROW: { key: string; name: string; Glyph: () => ReactNode; provider?: SsoProvider }[] = [
  { key: "google", name: "Google", Glyph: GoogleGlyph, provider: "Google" },
  { key: "microsoft", name: "Microsoft", Glyph: MicrosoftGlyph, provider: "Microsoft" },
  { key: "github", name: "GitHub", Glyph: GitHubGlyph },
];

function SsoButtons({ onClick, disabled }: { onClick: (p: SsoProvider) => void; disabled: boolean }) {
  const live = enabledSsoProviders();
  return (
    <div className="sso-row">
      {SSO_ROW.map(({ key, name, Glyph, provider }) => {
        const on = !!provider && live.includes(provider);
        return (
          <button
            key={key}
            type="button"
            className={`sso-icon${on ? "" : " is-soon"}`}
            onClick={() => on && provider && onClick(provider)}
            disabled={disabled || !on}
            aria-label={on ? `Continue with ${name}` : `Sign in with ${name} (coming soon)`}
            title={on ? `Continue with ${name}` : `Sign in with ${name} is coming soon`}
          >
            <Glyph />
          </button>
        );
      })}
    </div>
  );
}

function Flash({ tone, children }: { tone: "danger" | "success" | "info"; children: ReactNode }) {
  const Icon = tone === "danger" ? AlertCircle : Info;
  return (
    <div className={`flash flash-${tone}`} role={tone === "danger" ? "alert" : "status"}>
      {tone !== "success" && <Icon size={16} aria-hidden="true" />}
      <span>{children}</span>
    </div>
  );
}

// Decorative arcs and dot grid at the foot of the left rail, in brand colours
function RailDecor() {
  return (
    <svg aria-hidden="true" viewBox="0 -8 480 208" className="rail-decor">
      <defs>
        <linearGradient id="xq-arc-primary" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="var(--brand-primary)" stopOpacity="0.6" />
          <stop offset="100%" stopColor="var(--brand-primary)" stopOpacity="0.05" />
        </linearGradient>
        <linearGradient id="xq-arc-lime" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="var(--brand-accent)" stopOpacity="0.9" />
          <stop offset="100%" stopColor="var(--brand-accent)" stopOpacity="0.1" />
        </linearGradient>
        <pattern id="xq-dots" width="12" height="12" patternUnits="userSpaceOnUse">
          <circle cx="1.5" cy="1.5" r="0.9" fill="var(--brand-text-tertiary)" fillOpacity="0.35" />
        </pattern>
      </defs>
      <rect x="0" y="40" width="320" height="140" fill="url(#xq-dots)" />
      <path d="M0 200 A 200 200 0 0 1 200 0" fill="none" stroke="url(#xq-arc-primary)" strokeWidth="2" />
      <path d="M0 200 A 140 140 0 0 1 140 60" fill="none" stroke="url(#xq-arc-lime)" strokeWidth="2" />
      <path d="M0 200 A 80 80 0 0 1 80 120" fill="none" stroke="url(#xq-arc-primary)" strokeWidth="1.5" />
      <circle cx="200" cy="0" r="3.5" fill="var(--brand-primary)" />
      <circle cx="140" cy="60" r="3" fill="var(--brand-accent)" />
      <circle cx="80" cy="120" r="2" fill="var(--brand-primary)" fillOpacity="0.7" />
    </svg>
  );
}
